"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VerticalTabList = exports.useVerticalTabList = exports.buildId = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importDefault(require("react"));
const usePrevious_1 = require("../../hooks/usePrevious");
function Tab(_a) {
    var { onClick, onFocus, index, element, title, label } = _a, props = tslib_1.__rest(_a, ["onClick", "onFocus", "index", "element", "title", "label"]);
    const ref = react_1.default.useRef(null);
    const handleClick = react_1.default.useCallback(() => onClick(index), [index, onClick]);
    const handleFocus = react_1.default.useCallback(() => onFocus(index), [index, onFocus]);
    return (react_1.default.createElement("div", { ref: ref, "aria-selected": props.active, "aria-label": buildId('tab', label), className: props.className, id: props.id, onBlur: props.onBlur, onClick: handleClick, onFocus: props.active ? undefined : handleFocus, onTouchEnd: handleClick, role: 'tab', tabIndex: props.tabIndex, title: title !== null && title !== void 0 ? title : label }, element || title || label));
}
function buildId(...parts) {
    return parts.join('-');
}
exports.buildId = buildId;
const TabListActions = ['ArrowUp', 'ArrowDown', 'Home', 'End'];
function useVerticalTabList(_a) {
    var { testId, tabs, initialActiveIndex, ariaLabel, tabClassName, tabListClassName, tabPanelClassName, overrideActiveIndex, onActiveIndexChanged: onActiveIndexChangedDoNotUseDirectly } = _a, props = tslib_1.__rest(_a, ["testId", "tabs", "initialActiveIndex", "ariaLabel", "tabClassName", "tabListClassName", "tabPanelClassName", "overrideActiveIndex", "onActiveIndexChanged"]);
    const tabListRef = react_1.default.useRef(null);
    const max = react_1.default.useMemo(() => tabs.length, [tabs]);
    const [state, setState] = react_1.default.useState({
        activeIndex: Math.min(Math.max(initialActiveIndex, 0), max - 1),
        focusedIndex: -1,
    });
    const setActiveIndex = react_1.default.useCallback((index) => setState({ activeIndex: index, focusedIndex: index }), [setState]);
    const setFocusedIndex = react_1.default.useCallback((index) => setState(({ activeIndex }) => ({ activeIndex, focusedIndex: index })), [setState]);
    const buildTabId = react_1.default.useCallback((index) => buildId(testId, `tab${index + 1}`), [testId]);
    const onKeyDown = react_1.default.useCallback(e => {
        const key = e.key;
        if (TabListActions.includes(key)) {
            e.preventDefault && e.preventDefault();
            e.stopPropagation && e.stopPropagation();
            setState((prev) => {
                switch (key) {
                    case 'ArrowUp': {
                        const v = (max + prev.activeIndex - 1) % max;
                        return { activeIndex: v, focusedIndex: v };
                    }
                    case 'ArrowDown': {
                        const v = (prev.activeIndex + 1) % max;
                        return { activeIndex: v, focusedIndex: v };
                    }
                    case 'Home': {
                        return { activeIndex: 0, focusedIndex: 0 };
                    }
                    case 'End': {
                        const v = max - 1;
                        return { activeIndex: v, focusedIndex: v };
                    }
                }
            });
        }
    }, [max]);
    const onBlur = react_1.default.useCallback(() => setFocusedIndex(-1), [setFocusedIndex]);
    const onActiveIndexChanged = react_1.default.useCallback(onActiveIndexChangedDoNotUseDirectly, [
        onActiveIndexChangedDoNotUseDirectly,
    ]);
    const prev = (0, usePrevious_1.usePrevious)(state).current;
    react_1.default.useEffect(() => {
        var _a, _b;
        if (state.focusedIndex > -1 && state.focusedIndex !== (prev === null || prev === void 0 ? void 0 : prev.focusedIndex)) {
            (_b = (_a = tabListRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(`[role="tab"]`).item(state.focusedIndex)) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [state.activeIndex, state.focusedIndex, onActiveIndexChanged, prev]);
    react_1.default.useEffect(() => {
        if (state.activeIndex !== (prev === null || prev === void 0 ? void 0 : prev.activeIndex)) {
            onActiveIndexChanged(state.activeIndex);
        }
    }, [state.activeIndex, onActiveIndexChanged, prev === null || prev === void 0 ? void 0 : prev.activeIndex]);
    const indexOfTabbableTab = state.focusedIndex > -1 ? state.focusedIndex : state.activeIndex;
    const reset = react_1.default.useCallback(() => setState({ activeIndex: 0, focusedIndex: -1 }), [setState]);
    const element = (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("div", Object.assign({}, props),
            react_1.default.createElement("div", { "aria-label": ariaLabel, onKeyDown: onKeyDown, ref: tabListRef, role: 'tablist', className: tabListClassName }, tabs.map((tab, i) => {
                return (react_1.default.createElement(react_1.default.Fragment, { key: i },
                    tab.tabContent.heading,
                    react_1.default.createElement(Tab, Object.assign({ id: buildTabId(i), index: i, active: overrideActiveIndex ? false : state.activeIndex === i, onBlur: onBlur, onClick: setActiveIndex, onFocus: setFocusedIndex, tabIndex: overrideActiveIndex ? -1 : (i === indexOfTabbableTab ? 0 : -1), className: tabClassName }, tab.tabContent))));
            })))));
    return { element, reset };
}
exports.useVerticalTabList = useVerticalTabList;
function VerticalTabList(props) {
    const { element } = useVerticalTabList(props);
    return element;
}
exports.VerticalTabList = VerticalTabList;
//# sourceMappingURL=VerticalTabList.js.map